<template>
  <div class="item-container">
    <v-card class="card-property">
      <v-toolbar
        :color="color"
        dark>
        <v-icon
          v-if="icon"
          :small="smallIcon">
          {{ icon }}
        </v-icon>

        <v-toolbar-title>{{ title }}</v-toolbar-title>

        <v-spacer />
        <v-hover>
          <div slot-scope="{ hover }">
            <v-text-field
              v-if="hover"
              style="max-width: 160px"
              v-model="search"
              :append-icon="search ? '' : 'search'"
              flat
              clearable
              @click:clear="clearSearch" />
            <v-badge v-else>
              <template
                v-if="search"
                slot="badge">
                <v-icon small>
                  {{ $t('serverModel.components.field.propertyCard.clear','clear') }}
                </v-icon>
              </template>
              <v-icon>
                {{ $t('serverModel.components.field.propertyCard.search','search') }}
              </v-icon>
            </v-badge>
          </div>
        </v-hover>
      </v-toolbar>
      <slot name="header" />
      <v-list
        class="v-overflow full-size"
        dense>
        <component
          v-model="searchItems"
          :group="dragGroup"
          :sort="false"
          :is="component">
          <v-list-tile
            v-for="(item, index) in searchItems"
            :key="index"
            :class="dragGroup && dragGroup.pull && 'draggable'"
            @click="onClick"
            @dblclick.native="onDbClick(item)">
            <v-list-tile-action v-if="activeDeleteSelection">
              <v-checkbox
                v-model="selectedItems[item.id]"
                @change="raiseHasSelectedItems" />
            </v-list-tile-action>
            <v-list-tile-content :title="`\n name: ${item.name} \nlabel: ${item.label}\ndescription: ${item.description ? item.description.substring(0, 50): ''}...`">
              <v-list-tile-title
                v-text="item.label || item.name"
                :class="item.aggregator && 'card-title'" />
            </v-list-tile-content>
            <v-list-tile-content
              v-if="item.aggregator"
              :title="item.aggregator">
              <v-list-tile-title v-text="item.aggregator" />
            </v-list-tile-content>
            <v-list-tile-action>
              <div class="item-action">
                <v-icon :title="item.type">
                  {{ typeToIcon(item.type) }}
                </v-icon>

                <slot
                  name="item-action"
                  :item="item" />
              </div>
            </v-list-tile-action>
          </v-list-tile>
        </component>
      </v-list>
      <template v-if="showFooter">
        <v-card-actions>
          <v-menu
            v-if="!activeDeleteSelection"
            class="icon-action"
            top
            offset-y>
            <v-btn
              icon
              small
              slot="activator">
              <v-icon>more_vert</v-icon>
            </v-btn>

            <div class="content-menu">
              <v-icon
                @click="activeDeleteSelection = true"
                :title="$t('serverModel.components.field.propertyCard.deleteAlltitle','delete all')"
                color="red"
                small>
                fas fa-trash
              </v-icon>
              <!--              <v-icon-->
              <!--                      @click="deleteItem(item)"-->
              <!--                      class="icon-action"-->
              <!--                      title="delete"-->
              <!--                      color="red"-->
              <!--                      small>-->
              <!--                fas fa-trash-->
              <!--              </v-icon>-->
            </div>
          </v-menu>
          <template v-else>
            <v-btn
              @click="deleteSelection"
              :disabled="!hasSelectedItems"
              color="error"
              small>
              {{ $t('serverModel.components.field.propertyCard.delete','Delete') }}
            </v-btn>
            <v-btn
              @click="activeDeleteSelection = false"
              small>
              {{ $t('serverModel.components.field.propertyCard.cancel','Cancel') }}
            </v-btn>
          </template>
          <v-spacer />
          <small>{{ groupedItems.length }}</small>
        </v-card-actions>
      </template>
    </v-card>
  </div>
</template>

<script>
import { bizTypesPromise } from '../../services'
import { orderBy } from 'lodash'
import draggable from 'vuedraggable'
const defaultAggregator = 'Sum'

export default {
  asyncComputed: {
    bizTypesResponse () {
      return bizTypesPromise()
    }
  },
  sync: ['value'],
  props: {
    smallIcon: {
      type: Boolean,
      default: true,
      required: false
    },
    showFooter: {
      type: Boolean,
      default: false,
      required: false
    },
    editCommand: {
      type: Function,
      default: null,
      required: false
    },
    dragGroup: {
      type: Object,
      default: null,
      required: false
    },
    color: {
      type: String,
      default: '',
      required: false
    },
    icon: {
      type: String,
      default: '',
      required: false
    },
    title: {
      type: String,
      default: '',
      required: false
    },
    group: {
      type: String,
      default: '',
      required: false
    }
  },
  components: {
    draggable
  },
  data () {
    return {
      search: '',
      selectedItems: {},
      hasSelectedItems: false,
      activeDeleteSelection: false
    }
  },
  watch: {
    activeDeleteSelection () {
      const selectedItems = {}
      this.groupedItems.forEach(i => {
        selectedItems[i.id] = true
      })
      this.selectedItems = selectedItems
      this.raiseHasSelectedItems()
    }
  },
  computed: {
    types () {
      return this.bizTypesResponse ? this.bizTypesResponse.content : []
    },
    groupedItems () {
      return this.value.filter(v => (!this.group || v.group === this.group))
    },
    searchItems: {
      get () {
        return orderBy(this.groupedItems.filter(v => !this.search || ~v.name.toLowerCase().indexOf(this.search.toLowerCase())), [i => i.name.toLowerCase()], 'name')
      },
      set (items) {
        const itemFromCatalog = items.find(i => this.value.indexOf(i) === -1)
        if (itemFromCatalog) {
          this.value = [
            ...this.value,
            {
              ...itemFromCatalog,
              aggregator: this.group === 'MEZ' ? defaultAggregator : undefined,
              group: this.group
            }
          ]
        } else {
          const itemFromOtherGroup = items.find(i => this.groupedItems.indexOf(i) === -1)
          if (itemFromOtherGroup) {
            itemFromOtherGroup.group = this.group
            itemFromOtherGroup.aggregator = this.group === 'MEZ' ? defaultAggregator : undefined
          }
        }
      }
    },
    component () {
      return this.dragGroup ? 'draggable' : 'div'
    }
  },
  methods: {
    async deleteAll () {
      const res = await this.$dialog.confirm({
        text: this.$t('serverModel.components.field.propertyCard.deleteAll', `Do you really want to delete all {title}?`, { title: this.title }),
        title: this.$t('serverModel.components.field.propertyCard.warning', `Warning`)
      })
      if (res) {
        this.value = this.value.filter(i => this.groupedItems.indexOf(i) === -1)
      }
    },
    async deleteSelection () {
      const res = await this.$dialog.confirm({
        text: this.$t('serverModel.components.field.propertyCard.deleteSelected', `Do you really want to delete selected {title}?`, { title: this.title }),
        title: this.$t('serverModel.components.field.propertyCard.warning', `Warning`)
      })
      if (res) {
        this.value = this.value.filter(i => !this.selectedItems[i.id])
        this.activeDeleteSelection = false
      }
    },
    onClick () {},
    onDbClick (item) {
      if (this.editCommand && !this.activeDeleteSelection) {
        this.editCommand(item)
      }
    },
    clearSearch () {
      this.search = ''
    },
    typeToIcon (type) {
      let icon
      if (this.types) {
        const t = this.types.find(t => t.name === type)
        if (t) {
          icon = t.icon
        }
      }
      return icon || 'fas fa-text-width'
    },
    raiseHasSelectedItems () {
      let hasSelectedItems = false
      for (const key in this.selectedItems) {
        if (!hasSelectedItems) {
          const val = this.selectedItems[key]
          if (val) {
            hasSelectedItems = true
          }
        }
      }
      this.hasSelectedItems = hasSelectedItems
    }
  }
}
</script>

<style lang="scss" scoped>
    @import "~common-assets/scss/we";

    .item-container {
        padding: 5px;
        height: 365px;
        width: 100%;
        overflow: hidden;
        .card-property {
            @extend .full-size;
            @extend .flex-column;
            overflow: hidden;
            .card-title {
              width: 100px
            }
        }
        .item-action {
          @extend .flex-row;
        }
    }
    .v-list {
      > div {
        height: 100%;
      }
    }
</style>

<style lang="scss">
  @import "~common-assets/scss/we";

  .draggable {
    .v-list__tile {
      cursor : move !important;
    }
  }
  .content-menu {
    @extend .flex-column;
    padding: 10px;
    background: white;
    .v-icon {
      margin: 5px;
    }
  }
</style>
